import { useState } from 'react';
import { TicketLogin, TicketShell, TicketStage } from '@matix/ui';
import { useAuthStore } from '../../lib/authStore';
import { useLang } from '../../lib/lang';
import { authErrorMessage } from '../../lib/errors';

/**
 * Appointments sign-in.
 *
 * Presentation is the shared ticket from @matix/ui, matching the till, the
 * KDS and the admin console. The staged flow this app has always had —
 * credentials, then subsidiary → branch → till, each auto-advanced when there
 * is only one option — now runs on the same paper via `TicketStage` instead
 * of swapping to a differently-styled card halfway through.
 */

export function LoginScreen(): JSX.Element {
  const s = useAuthStore();
  const { t, lang, toggle } = useLang();

  const [values, setValues] = useState({
    tenant: '',
    username: '',
    password: '',
    remember: true,
  });

  const consoleLabel = {
    en: 'Appointments · Powered by MatixERP',
    ar: 'المواعيد · مدعوم من MatixERP',
  };

  // useLang exposes a toggle, not a setter — bounce through it so the ticket's
  // two-button control still lands on the language the operator picked.
  const onLangChange = (next: 'en' | 'ar'): void => {
    if (next !== lang) toggle();
  };

  const errorText = s.error ? t(authErrorMessage(s.error)) : null;

  if (s.stage === 'anonymous') {
    return (
      <TicketLogin
        lang={lang}
        onLangChange={onLangChange}
        console={consoleLabel}
        title={{ en: 'Sign in to appointments', ar: 'تسجيل الدخول إلى المواعيد' }}
        tenantLabel={{ en: 'Workspace', ar: 'مساحة العمل' }}
        values={values}
        onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
        onSubmit={() => void s.login(values.tenant.trim(), values.username.trim(), values.password)}
        busy={s.loading}
        error={errorText}
      />
    );
  }

  const stage =
    s.stage === 'subsidiary'
      ? {
          title: t({ en: 'Choose subsidiary', ar: 'اختر الشركة' }),
          options: s.subsidiaries.map((x) => ({ id: x.id, label: x.name, sub: x.code })),
          onPick: (id: number | string) => void s.chooseSubsidiary(Number(id)),
        }
      : s.stage === 'branch'
        ? {
            title: t({ en: 'Choose branch', ar: 'اختر الفرع' }),
            options: s.branches.map((x) => ({
              id: x.id,
              label: x.name,
              sub: x.has_cash_drawer
                ? undefined
                : x.drawer_inactive
                  ? t({ en: 'Cash Drawer inactive', ar: 'درج النقديه غير نشط' })
                  : t({ en: 'no Cash Drawer', ar: 'لا يوجد درج النقديه' }),
            })),
            onPick: (id: number | string) => void s.chooseBranch(Number(id)),
          }
        : {
            title: t({ en: 'Choose Cash Drawer', ar: 'اختر درج النقديه' }),
            options: s.drawers.map((x) => ({ id: x.id, label: x.name, sub: x.code })),
            onPick: (id: number | string) => void s.chooseDrawer(Number(id)),
          };

  return (
    <TicketShell lang={lang} onLangChange={onLangChange} console={consoleLabel}>
      {errorText && (
        <div className="tkt-notice error" role="alert">
          <span>{errorText}</span>
        </div>
      )}
      <TicketStage
        lang={lang}
        title={stage.title}
        options={stage.options}
        onPick={stage.onPick}
        onBack={s.back}
        busy={s.loading}
        backLabel={t({ en: '← Back', ar: 'رجوع →' })}
        emptyLabel={t({ en: 'No options available.', ar: 'لا توجد خيارات متاحة.' })}
      />
    </TicketShell>
  );
}
